import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Trash2, Database, Package, Building, Boxes, School as SchoolIcon, ShieldAlert } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { getSupplyExecutiveDashboard } from '../api';

const numFmt = (n) => (n == null ? 'غير متوفر' : Number(n).toLocaleString('en-US'));

export default function SettingsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wiping, setWiping] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState(false);
  const [wipeText, setWipeText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await getSupplyExecutiveDashboard()); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doWipe = async () => {
    if (wipeText !== 'حذف') return;
    setWiping(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supply/wipe`, { method: 'POST' });
      await load();
      setWipeConfirm(false); setWipeText('');
    } finally { setWiping(false); }
  };

  return (
    <div dir="rtl" className="supply-theme min-h-screen flex">
      <Sidebar />
      <main className="flex-1 min-w-0 p-6 md:p-8" data-testid="settings-page">
        <div className="mb-6">
          <div className="text-[11px] font-semibold tracking-widest" style={{ color: 'var(--se-violet-2)' }}>SETTINGS</div>
          <h1 className="se-title mt-1">الإعدادات</h1>
          <div className="se-subtitle">مراقبة النظام وإدارة بيانات التوريد</div>
        </div>

        {loading ? (
          <div className="se-panel p-12 text-center"><Loader2 size={26} className="animate-spin mx-auto" style={{ color: '#9D8CFF' }} /></div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="se-panel p-5" data-testid="system-status">
                <div className="text-[13px] font-bold mb-3" style={{ color: '#F1EEFB' }}>حالة النظام</div>
                <div className="space-y-2 text-[12px]">
                  <StatusRow label="خدمة الاستيراد" value="نشطة" ok />
                  <StatusRow label="قاعدة البيانات" value="متصلة" ok />
                  <StatusRow label="إصدار التطبيق" value="v6.3 · Feb 2026" />
                </div>
              </div>
              <div className="se-panel p-5" data-testid="data-summary">
                <div className="text-[13px] font-bold mb-3" style={{ color: '#F1EEFB' }}>ملخص بيانات التوريد</div>
                <div className="space-y-2 text-[12px]">
                  <StatusRow label="إجمالي الموردين" value={numFmt(data?.kpis?.totalSuppliers)} icon={Building} />
                  <StatusRow label="إجمالي المدارس" value={numFmt(data?.kpis?.totalSchools)} icon={SchoolIcon} />
                  <StatusRow label="إجمالي الأصناف" value={numFmt(data?.kpis?.totalItems)} icon={Boxes} />
                  <StatusRow label="إجمالي السجلات" value={numFmt(data?.dataQuality?.total_records)} icon={Database} />
                </div>
              </div>
            </div>

            {/* Danger zone */}
            <div className="se-panel p-5" style={{ border: '1px solid rgba(248,113,113,0.32)' }} data-testid="danger-zone">
              <div className="flex items-start gap-3 mb-3">
                <ShieldAlert size={20} style={{ color: '#F87171' }} />
                <div>
                  <div className="text-[14px] font-bold" style={{ color: '#F87171' }}>منطقة الخطر</div>
                  <div className="text-slate-400 text-[11.5px] mt-1">
                    حذف جميع بيانات التوريد. لن يتم حذف كود التطبيق أو الإعدادات.
                    ستبقى جميع الملفات المستوردة سابقاً محذوفة من قاعدة البيانات فقط.
                  </div>
                </div>
              </div>
              {!wipeConfirm ? (
                <button className="se-btn" onClick={() => setWipeConfirm(true)}
                  style={{ background: 'rgba(248,113,113,0.14)', color: '#F87171', border: '1px solid rgba(248,113,113,0.32)' }}
                  data-testid="wipe-open-btn">
                  <Trash2 size={13} /> حذف جميع بيانات التوريد
                </button>
              ) : (
                <div>
                  <div className="text-amber-200 text-[12px] mb-2">
                    اكتب <b>حذف</b> للتأكيد على حذف <b>{numFmt(data?.dataQuality?.total_records)}</b> سجل توريد.
                  </div>
                  <div className="flex items-center gap-2">
                    <input className="se-input flex-1" placeholder="اكتب: حذف" value={wipeText}
                      onChange={(e) => setWipeText(e.target.value)} data-testid="wipe-confirm-input" />
                    <button onClick={doWipe} disabled={wipeText !== 'حذف' || wiping}
                      className="se-btn disabled:opacity-40"
                      style={{ background: '#F87171', color: '#FFF', border: '1px solid rgba(255,255,255,0.14)' }}
                      data-testid="wipe-execute-btn">
                      {wiping ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} تأكيد الحذف
                    </button>
                    <button onClick={() => { setWipeConfirm(false); setWipeText(''); }} className="se-btn se-btn-ghost">إلغاء</button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function StatusRow({ label, value, ok, icon: Icon }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-400 flex items-center gap-2">
        {Icon && <Icon size={12} />}
        {label}
      </span>
      <span className="font-bold tabular-nums" style={{ color: ok ? '#34D399' : '#F1EEFB' }}>{value}</span>
    </div>
  );
}
